import React, { Suspense, useEffect, useState, type ReactNode } from 'react';
import Image from 'next/image';
import { ButtonProviders, Providers } from '@/components/Auth/utils';
import { signOut } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import { getSignUpRoute } from '@/lib/utils/app-routes';
import { buildSignInCallbackPath } from '@/lib/auth/postLoginDestination';
import { stashCliFlow } from '@/lib/utils/cliAuth';
import posthog from '@/lib/posthog';
import { AuthTestimonial } from '@/components/Auth/AuthTestimonial';
import { perfectlyNineties } from '@/lib/fonts';
import { LazyMotion, m } from 'framer-motion';
import { domAnimation } from 'framer-motion';

function SignInProvidersContent({
  providers,
  isPopUp,
  callbackUrl,
  notice,
}: {
  providers: Providers[];
  isPopUp: boolean;
  callbackUrl?: string;
  notice?: ReactNode;
}) {
  const params = useSearchParams();
  const [redirectUri, setRedirectUri] = useState<string>('');
  const [extensionName, isCli, cliRedirect, cliState] = [
    params.get('extensionName'),
    params.get('cli'),
    params.get('cli_redirect'),
    params.get('cli_state'),
  ];
  // Google sign-in is gated behind ?google=true for now (mirrors the ?cli=true
  // pattern). When enabled, the Google button is appended to the provider list.
  const googleEnabled = params.get('google') === 'true';
  const effectiveProviders: Providers[] = googleEnabled
    ? [...providers, 'google']
    : providers;

  useEffect(() => {
    const constructRedirectUri = () => {
      let callbackURL: string;
      if (extensionName) {
        callbackURL =
          callbackUrl || `/extension/login?extensionName=${extensionName}`;
      } else if (isPopUp) {
        callbackURL = '/home';
      } else {
        callbackURL = callbackUrl || '/home';
      }
      return buildSignInCallbackPath({
        callbackURL,
        extensionName,
        cli: isCli === 'true',
        cliRedirect,
        cliState,
      });
    };

    // Stash before any sign-in click so onboarding still finds the CLI flag.
    if (isCli === 'true') stashCliFlow({ cliRedirect, cliState });

    const uri = constructRedirectUri();
    setRedirectUri(uri);

    posthog.capture('login_flow_started', {
      flow_type: extensionName
        ? 'extension'
        : isCli
          ? 'cli'
          : isPopUp
            ? 'popup'
            : 'standard',
      has_callback_url: !!callbackUrl,
      redirect_uri: uri,
    });
  }, [extensionName, isPopUp, callbackUrl, isCli, cliRedirect, cliState]);

  const handleSignUpClick = () => {
    posthog.capture('login_to_signup_navigation', {
      current_path: window.location.pathname,
      has_extension: !!extensionName,
      is_cli_flow: isCli === 'true',
    });

    // Preserve invite destination when bouncing login → signup, otherwise the
    // join-workspace callback dies and signup sends them to create-org onboarding.
    const inviteCallback =
      typeof callbackUrl === 'string' &&
      callbackUrl.startsWith('/join-workspace')
        ? callbackUrl
        : new URLSearchParams(window.location.search).get(
            'callbackURLAfterSignIn'
          );
    const callbackURLAfterSignUp =
      inviteCallback && inviteCallback.startsWith('/join-workspace')
        ? inviteCallback
        : !window?.location.pathname.includes('login') &&
            !window?.location.pathname.includes('signup')
          ? window?.location.pathname
          : '';

    const signUpRoute = getSignUpRoute({
      callbackURLAfterSignUp: callbackURLAfterSignUp,
      signup: 'true',
      extensionName: extensionName || undefined,
      cli: isCli || undefined,
      cliRedirect: cliRedirect || undefined,
      cliState: cliState || undefined,
    });

    signOut({
      callbackUrl: signUpRoute,
    });
  };

  return (
    <LazyMotion features={domAnimation} strict>
      <m.div
        className="flex flex-col md:flex-row w-full h-full rounded-[12px] border border-ds-brand-darker shadow-[0px_12px_32px_0px_#1a1d14] overflow-hidden"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* Left panel */}
        <div className="flex flex-col flex-1 bg-ds-surface-off-white p-6 overflow-y-auto">
          <div className="flex flex-col items-center justify-between flex-1 p-6 rounded-[12px]">
            {/* Logo */}
            <m.div
              className="shrink-0 self-center"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <Image
                src="/assets/onboarding/Entelligence.svg"
                alt="Entelligence"
                width={162}
                height={24}
                className="h-6 w-auto opacity-40"
                style={{
                  filter:
                    'brightness(0) saturate(100%) invert(22%) sepia(17%) saturate(800%) hue-rotate(55deg) brightness(85%)',
                }}
                preload
              />
            </m.div>

            {/* Form area */}
            <div className="flex flex-col items-center gap-8 w-full max-w-[520px] mt-12">
              <m.div
                className="flex flex-col items-center text-center gap-3 w-full mb-14 -mt-6"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.4,
                  delay: 0.15,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <h1
                  className={`${perfectlyNineties.className} text-[36px] font-medium text-ds-text-darker leading-[1.3] tracking-[0.72px] text-center w-full`}
                >
                  Welcome Back!
                </h1>
                <p className="font-instrument-sans text-base font-medium text-ds-text-subtle leading-[1.45] text-center w-full">
                  Scale your engineering maturity with intelligent code reviews,
                  performance and contextual answers that lives where you work!
                </p>
              </m.div>

              <m.div
                className="flex flex-col gap-3 w-full"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.4,
                  delay: 0.25,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                {notice}
                {effectiveProviders.map((provider) => (
                  <ButtonProviders
                    redirectUri={redirectUri}
                    provider={provider}
                    isSignIn={true}
                    key={provider}
                  />
                ))}
              </m.div>

              {!isPopUp && (
                <m.p
                  className="font-instrument-sans text-xs font-medium text-ds-text-subtle"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.35 }}
                >
                  <span>New to the platform? </span>
                  <button
                    type="button"
                    className="font-medium text-ds-brand-default underline hover:opacity-80 focus:outline-none"
                    onClick={handleSignUpClick}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSignUpClick();
                      }
                    }}
                  >
                    Sign-up
                  </button>
                </m.p>
              )}
            </div>

            <m.p
              className="font-instrument-sans text-xs font-medium text-ds-text-subtle text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.4 }}
            >
              By signing in, you agree to our{' '}
              <a
                href="https://entelligence.ai/terms-and-condition"
                target="_blank"
                rel="noopener noreferrer"
                className="text-ds-brand-subtle hover:underline"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="https://entelligence.ai/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-ds-brand-subtle hover:underline"
              >
                Privacy Policy
              </a>
              .
            </m.p>
          </div>
        </div>

        <AuthTestimonial />
      </m.div>
    </LazyMotion>
  );
}

export const SignInProviders = (props: {
  providers: Providers[];
  isPopUp: boolean;
  callbackUrl?: string;
  notice?: ReactNode;
}) => (
  <Suspense fallback={<div className="animate-pulse h-64" />}>
    <SignInProvidersContent {...props} />
  </Suspense>
);
